import type { QueryResultLike } from '@krp/client-runtime'

import type { AccessGrants, ActionVm, ResourceSummary } from '../../resources/model/types'
import { resourcePermissionKey, SUPER_ROLE } from './roles'
import type { MyAccess } from './types'

/**
 * Права человека глазами экранов. Только ОТРАЖЕНИЕ сервера: кнопка, которую
 * спрятали здесь, всё равно проверяется сервером, а кнопка, которую здесь
 * показали, может получить 403 — его текст показывается дословно.
 */
export const UNKNOWN_GRANTS: AccessGrants = {
  permissions: new Set(),
  superRole: false,
  registryRead: false,
  registryWrite: false,
  known: false,
}

/**
 * Права реестра ресурсов в ключах матрицы Platform (AU1b): их отдаёт
 * `access/me/capabilities` тем, кому выдано, суперроли — тоже ими же, и
 * `access_override` `-key` снимает их даже у неё. Поэтому реестр решается по
 * ключам, а не по роли.
 */
export const REGISTRY_READ = 'platform:registry:read'
export const REGISTRY_WRITE = 'platform:registry:write'

/**
 * Поиск по фото в тестовом каталоге замеров. Продавцу он не нужен, поэтому у
 * переключателя «Тестирование» своё право: у суперроли оно есть, другим ролям
 * его выдаёт администратор в матрице.
 */
export const SEARCH_TEST = 'platform:search:test'

/**
 * Менять общий каталог: создание товара, правка, фото (RP-FB50, ключ матрицы `platform:catalog:write`). Сканирование по нему решает,
 * показывать ли «Создать новый товар» и «Прикрепить фото»; сервер проверит право сам.
 */
export const CATALOG_WRITE = 'platform:catalog:write'

/**
 * Вкладка «Тестирование» (RP-FB29): тестовые товары и замеры поиска по фото. Выдаётся
 * суперроли и тем, кому выдал администратор; без него пункта меню нет.
 */
export const TESTING_SEARCH = 'platform:testing:search'

/** Режим обновления касс (RP-MNT): пульт в «Настройках» — список открытых РМК и включение/выключение режима. */
export const MAINTENANCE_MANAGE = 'platform:maintenance:manage'

/** Настройки раздела «Фото» (RP-CACHE-0): сроки временных загрузок и кадров поиска, потолок кэша; пункт «Фото» в «Настройках» только по праву. */
export const PHOTO_SETTINGS = 'platform:photo:settings'

const grantsCache = new WeakMap<MyAccess, AccessGrants>()

/** Права из ответа «мои права». Мемоизация по объекту ответа: тот же ответ — тот же объект. */
export function grantsOf(query: QueryResultLike<MyAccess>): AccessGrants {
  const data = query.data
  if (data === undefined || query.isError) return UNKNOWN_GRANTS
  const cached = grantsCache.get(data)
  if (cached !== undefined) return cached
  const permissions = new Set(data.api)
  const grants: AccessGrants = {
    permissions,
    superRole: data.roles.includes(SUPER_ROLE),
    registryRead: permissions.has(REGISTRY_READ),
    registryWrite: permissions.has(REGISTRY_WRITE),
    known: true,
  }
  grantsCache.set(data, grants)
  return grants
}

export const ALLOWED: ActionVm = { allowed: true, reason: null }

export function denied(reason: string): ActionVm {
  return { allowed: false, reason }
}

const ACCESS_UNKNOWN = 'Права не получены от сервера'

/**
 * Право ресурса для человека: право ВКЛЮЧЕНО у ресурса (хранится в Platform и перекрывает даже суперроль) И ВЫДАНО
 * человеку (`<вид>:<право>` в его правах, суперроль — мимо матрицы). Это оценка
 * С ЗАПАСОМ: допуск роли к ресурсу (`role_resource_grants`) и запреты
 * `access_override` клиенту не видны, поэтому сервер всё равно может ответить
 * 403 `resource-not-granted`/`policy-denied` — его текст показывается дословно.
 * Список ресурсов сервер фильтрует по `resource_id` токена.
 */
export function resourceCapabilityAction(
  grants: AccessGrants,
  resource: ResourceSummary,
  capability: string,
): ActionVm {
  if (!resource.enabled) return denied('Ресурс выключен')
  const declared = resource.capabilities.find((item) => item.name === capability)
  // Нет ключа в списке прав РЕСУРСА — это не «вид не умеет»: вид право знает, а ресурс создан раньше, чем оно появилось
  // в манифесте. Сервер считает такое право невключённым даже для суперроли, поэтому причина названа прямо.
  if (declared === undefined) {
    return denied(
      `Права «${capability}» нет в списке прав ресурса — вид добавил его позже, чем ресурс создан. Включает администратор в редакторе ресурса`,
    )
  }
  if (!declared.enabled) return denied(`Право «${capability}» выключено у ресурса. Включает администратор в редакторе ресурса`)
  if (!grants.known) return denied(ACCESS_UNKNOWN)
  if (grants.superRole || grants.permissions.has(resourcePermissionKey(resource.kind, capability))) return ALLOWED
  return denied(`Вашей роли не выдано право «${capability}»`)
}

/** Ресурсы, где право доступно человеку, — для выбора источника/получателя, окон и фильтров. */
export function resourcesWithCapability(
  grants: AccessGrants,
  resources: readonly ResourceSummary[],
  capability: string,
): ResourceSummary[] {
  return resources.filter((resource) => resourceCapabilityAction(grants, resource, capability).allowed)
}

/**
 * Правка реестра: добавить, изменить, проверить связь — право
 * `platform:registry:write` из «моих прав». Суперроль сама по себе его не даёт:
 * сервер отдаёт ей ключ в том же списке, а личный запрет снимает его.
 */
export function registryManageAction(grants: AccessGrants): ActionVm {
  if (!grants.known) return denied(ACCESS_UNKNOWN)
  return grants.registryWrite ? ALLOWED : denied('Вашей роли не выдано право вести реестр ресурсов')
}

/**
 * Консоль прав (матрица ролей и допуск ролей к ресурсам) — только
 * администратор приложения: так решает сервер (RP-A3), отдельного ключа права
 * у консоли нет, поэтому здесь — роль.
 */
export function accessConsoleAction(grants: AccessGrants): ActionVm {
  if (!grants.known) return denied(ACCESS_UNKNOWN)
  return grants.superRole ? ALLOWED : denied('Права к ресурсам ведёт администратор приложения')
}
